import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { HomeFilter, HomeTab } from "../factories/works/models/home-enums";
import { WorkTableSort } from "../features/works/types/work-table";

type SortOrder = "asc" | "desc";

type UseHomeSearchParamsOptions = {
  defaultTab?: HomeTab;
  defaultSortBy?: WorkTableSort;
  defaultSortOrder?: SortOrder;
};

export const useHomeSearchParams = (options?: UseHomeSearchParamsOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultTab = options?.defaultTab ?? HomeTab.ThisWeek;
  const defaultSortBy = options?.defaultSortBy ?? WorkTableSort.StartDate;
  const defaultSortOrder = options?.defaultSortOrder ?? "desc";

  const activeTab =
    (searchParams.get("home-tab") as HomeTab | null) ?? defaultTab;

  const activeFilter =
    (searchParams.get("home-filter") as HomeFilter | null) ?? null;

  const pageRaw = Number(searchParams.get("page") ?? 1);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  const sortBy =
    (searchParams.get("sortBy") as WorkTableSort | null) ?? defaultSortBy;

  const sortOrder =
    (searchParams.get("sortOrder") as SortOrder | null) ?? defaultSortOrder;

  const updateParams = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      updateParams({ page: String(nextPage) });
    },
    [updateParams],
  );

  const setSort = useCallback(
    (nextSortBy: WorkTableSort, nextSortOrder: SortOrder) => {
      updateParams({
        sortBy: nextSortBy,
        sortOrder: nextSortOrder,
        page: "1",
      });
    },
    [updateParams],
  );

  const setActiveTab = useCallback(
    (nextTab: HomeTab) => {
      updateParams({
        "home-tab": nextTab,
        "home-filter": null,
        page: "1",
      });
    },
    [updateParams],
  );

  const setActiveFilter = useCallback(
    (nextFilter: HomeFilter | null) => {
      updateParams({
        "home-filter": nextFilter,
        page: "1",
      });
    },
    [updateParams],
  );

  return {
    activeTab,
    activeFilter,
    page,
    sortBy,
    sortOrder,
    setPage,
    setSort,
    setActiveTab,
    setActiveFilter,
    updateParams,
  };
};
